import React, { useState } from 'react';
import { products } from '../mock';
import { Button } from '../components/ui/button';
import { CheckCircle } from 'lucide-react';

export const Products = ({ onQuoteClick }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'uPVC', 'Aluminium'];

  const filteredProducts =
    selectedCategory === 'all'
      ? products
      : products.filter((p) => p.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Products
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Explore our comprehensive range of premium System Aluminium and
              uPVC doors and windows
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={
                  selectedCategory === category
                    ? 'bg-blue-900 hover:bg-blue-800'
                    : 'border-gray-300 hover:border-blue-900'
                }
              >
                {category === 'all' ? 'All Products' : category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="relative h-72 overflow-hidden group">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-blue-900 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {product.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {product.description}
                  </p>
                  <div className="space-y-2 mb-6">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle
                          className="text-green-500 flex-shrink-0 mr-2"
                          size={18}
                        />
                        <span className="text-sm text-gray-600">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={onQuoteClick}
                    className="w-full bg-blue-900 hover:bg-blue-800"
                  >
                    Get Quote
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                uPVC Products
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Our uPVC (unplasticized Polyvinyl Chloride) range offers
                exceptional thermal insulation, sound dampening, and
                weather-resistance. Perfect for residential applications where
                energy efficiency and comfort are priorities.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    Superior energy efficiency reduces utility costs
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    Low maintenance - no painting or special treatments needed
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    Excellent sound insulation for peaceful interiors
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    Available in multiple colors and finishes
                  </span>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Aluminium Products
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Our System Aluminium range provides unmatched strength, slim
                profiles, and modern aesthetics. Ideal for larger openings and
                commercial applications where durability and design are
                paramount.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    High strength-to-weight ratio for large installations
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    Slim frames maximize glass area and natural light
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    Corrosion-resistant for long-lasting performance
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    className="text-blue-900 mt-1 mr-3 flex-shrink-0"
                    size={20}
                  />
                  <span className="text-gray-600">
                    Contemporary look perfect for modern architecture
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Need Help Choosing the Right Product?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our experts are here to guide you through the selection process
          </p>
          <Button
            onClick={onQuoteClick}
            size="lg"
            className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Request Consultation
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Products;